import {
  UPLOAD_MAX_BYTES,
  UPLOAD_MAX_FILES,
  UPLOAD_MAX_RATIO,
  UPLOAD_MAX_UNCOMPRESSED_BYTES,
} from '@atlas/api-contract';

import { isApiError } from '@/lib/api/errors';
import { formatBytes } from '@/lib/format/bytes';

/**
 * Every way an upload can be refused, told apart.
 *
 * The API already returns a readable sentence for each rejection, and that
 * sentence carries the facts only the server has: *which* file has the unsafe
 * path, *which* HTML files it found instead of `index.html`, *how far* over a
 * limit the archive was. None of that can be reconstructed here, so it is never
 * paraphrased — it is shown verbatim as the `detail`.
 *
 * What the server cannot supply is what to do next, and that is the whole
 * reason this module exists. "No index.html was found" and "the archive expands
 * 400:1" are the same HTTP status and the same shape of message, but one is a
 * naming mistake fixed in ten seconds and the other means the archive is not
 * what it claims to be. Collapsing them into "Upload failed" throws away the
 * only part a designer standing at their laptop can act on, so each rejection
 * gets its own headline and its own remedy.
 *
 * Matching is done on the server's wording rather than on an error code because
 * the API is single-language English and returns messages, not codes (see
 * `lib/api/errors.ts`). The markers chosen are the stable parts of each
 * sentence: the `UPLOAD_MAX_*` environment variable names, and the phrases the
 * upload pipeline is built around. An unrecognised message still renders — as
 * itself, under a neutral headline — so a new server rejection degrades to "the
 * server's own words" rather than to nothing.
 */

export type UploadRejectionKind =
  /* Refused before the archive was read. */
  | 'no-file'
  /* Layout: the entry file (spec R1.2, R1.3). */
  | 'missing-index'
  | 'misplaced-index'
  | 'ambiguous-index'
  | 'empty-archive'
  | 'duplicate-entry'
  /* Hostile or unusable contents (spec R1.6). */
  | 'path-traversal'
  | 'unsafe-path'
  | 'symlink'
  | 'unsupported-entry'
  | 'encrypted'
  | 'corrupt'
  | 'archive-changed'
  /* Limits (spec R1.4). */
  | 'too-large'
  | 'too-many-files'
  | 'expands-too-much'
  | 'zip-bomb'
  /* Everything that is not about the archive itself. */
  | 'prototype-missing'
  | 'concurrent-upload'
  | 'storage'
  | 'network'
  | 'timeout'
  | 'cancelled'
  | 'unknown';

export interface UploadRejection {
  kind: UploadRejectionKind;
  /** Headline naming what was wrong, in the reader's terms. */
  title: string;
  /** What to change before uploading again. Written here, never by the server. */
  remedy: string;
  /** The server's own sentence, which names the file or the figure. */
  detail: string | null;
  /** Paths the server listed, pulled out so they can be rendered as a list. */
  files: string[];
  /**
   * Whether re-sending *this* archive could plausibly succeed. True only for
   * failures that are about the moment rather than the file — a dropped
   * connection, a lost race with another upload — so the dialog can offer
   * "Try again" instead of "Choose a different file".
   */
  canRetrySameFile: boolean;
}

/** How many listed paths to carry. Past this the list stops being readable. */
const MAX_LISTED_FILES = 20;

const unique = (values: readonly string[]): string[] => [...new Set(values)];

/** Every double-quoted run in a message — how the API names a specific entry. */
const quotedPaths = (message: string): string[] =>
  unique(
    [...message.matchAll(/"([^"]+)"/g)]
      .map((match) => match[1] ?? '')
      .filter((value) => value !== ''),
  );

/**
 * The comma-separated list the layout errors append, minus the "and 3 more"
 * tail the server adds when it truncates.
 */
const listedPaths = (message: string): string[] => {
  const match = /(?:HTML files found|first entries are):?\s*(.+?)\.(?:\s|$)/.exec(message);
  const list = match?.[1];

  if (list === undefined) {
    return [];
  }

  return unique(
    list
      .replace(/\s+and\s+\d+\s+more$/, '')
      .split(/,\s*/)
      .map((entry) => entry.trim())
      .filter((entry) => entry !== ''),
  );
};

/**
 * The files an `index.html` failure was decided by.
 *
 * Quoted candidates first — those are the exact entries the server weighed —
 * and the prose list only when there were none, which is the "no index.html at
 * all" case where the server lists what it found instead.
 */
const entryFileCandidates = (message: string): string[] => {
  const html = quotedPaths(message).filter((path) => /\.html?$/i.test(path));

  return html.length > 0 ? html : listedPaths(message);
};

interface RejectionRule {
  kind: UploadRejectionKind;
  /** Matched against the server's message. The stable part of the sentence. */
  test: RegExp;
  title: string;
  remedy: string;
  /** Pulls the paths the message names, when it names any. */
  files?: (message: string) => string[];
  canRetrySameFile?: boolean;
}

/**
 * Ordered most specific first: an encrypted entry is discovered while reading
 * the central directory, so its message must be matched before the generic
 * "could not be read as a ZIP archive" that wraps other read failures.
 */
const RULES: readonly RejectionRule[] = [
  {
    kind: 'no-file',
    test: /ZIP archive is required in the/i,
    title: 'No archive was sent',
    remedy: 'Choose a .zip file, then upload again.',
    canRetrySameFile: true,
  },
  {
    kind: 'encrypted',
    test: /is encrypted/i,
    title: 'The archive is password-protected',
    remedy:
      'Atlas cannot read an encrypted ZIP — it would have nothing to serve. Re-create the archive without a password.',
    files: quotedPaths,
  },
  {
    kind: 'symlink',
    test: /symbolic link/i,
    title: 'The archive contains a symbolic link',
    remedy:
      'A link can point anywhere outside the prototype, so the whole upload is refused rather than the one entry skipped. Replace the link with the file it points at and zip the folder again.',
    files: quotedPaths,
  },
  {
    kind: 'unsupported-entry',
    test: /contains (?:a socket|a block device|a character device|a named pipe|an entry that is not a regular file)/i,
    title: 'The archive contains something that is not a file',
    remedy:
      'Sockets, pipes and device nodes cannot be stored or served. Zip only the prototype’s files and folders.',
    files: quotedPaths,
  },
  {
    kind: 'path-traversal',
    test: /escapes the archive root|absolute path|drive-qualified path|falls outside the version prefix|not in canonical form/i,
    title: 'The archive contains a path that escapes it',
    remedy:
      'One entry is addressed outside the archive root, which is how a ZIP smuggles files somewhere they were not meant to go — so nothing at all was imported. Re-create the archive from inside the prototype folder.',
    files: quotedPaths,
  },
  {
    kind: 'unsafe-path',
    test: /Windows separators|control characters|character limit|empty path|normalises to nothing/i,
    title: 'The archive contains a path Atlas cannot store',
    remedy:
      'Rename the entry named below — plain names and forward slashes only — then zip the folder again.',
    files: quotedPaths,
  },
  {
    kind: 'ambiguous-index',
    test: /entry point is ambiguous|more than one index\.html/i,
    title: 'Which index.html opens the prototype is ambiguous',
    remedy:
      'Several index.html files were found and none is at the top level, so Atlas will not guess. Keep the one that opens the prototype at the root of the folder you zip.',
    files: entryFileCandidates,
  },
  {
    kind: 'misplaced-index',
    test: /index\.html must sit at the archive root/i,
    title: 'index.html is buried inside the archive',
    remedy:
      'Move it to the top of the archive, or zip a single folder with index.html directly inside it. Atlas strips one wrapper folder, not a whole path.',
    files: entryFileCandidates,
  },
  {
    kind: 'missing-index',
    test: /No index\.html was found/i,
    title: 'The archive has no index.html',
    remedy:
      'A prototype opens at index.html. Rename the file that should open it — the ones found are listed below — and upload again.',
    files: entryFileCandidates,
  },
  {
    kind: 'empty-archive',
    test: /archive is empty/i,
    title: 'The archive is empty',
    remedy: 'It must contain an index.html plus the assets that page loads.',
  },
  {
    kind: 'duplicate-entry',
    test: /more than one entry for/i,
    title: 'Two entries in the archive share one path',
    remedy:
      'The second would silently overwrite the first, so neither was written. Remove the duplicate and upload again.',
    files: quotedPaths,
  },
  {
    kind: 'archive-changed',
    test: /changed while it was being unpacked/i,
    title: 'The archive changed while it was being read',
    remedy: 'Nothing was saved. Upload the same file again.',
    canRetrySameFile: true,
  },
  {
    kind: 'corrupt',
    test: /could not be read as a ZIP archive/i,
    title: 'The file could not be read as a ZIP',
    remedy:
      'It is either not a ZIP archive or it arrived truncated. Re-create the archive and upload it again.',
  },
  {
    kind: 'zip-bomb',
    test: /UPLOAD_MAX_RATIO|compressed too aggressively/i,
    title: 'The archive expands far more than a prototype should',
    remedy: `Atlas refuses anything expanding past ${String(UPLOAD_MAX_RATIO)}:1, because that is the shape of a zip bomb rather than of a prototype. Check for a placeholder file padded with repeated data.`,
  },
  {
    kind: 'expands-too-much',
    test: /UPLOAD_MAX_UNCOMPRESSED_BYTES|expands to too much data/i,
    title: 'The archive unpacks to too much data',
    remedy: `Unpacked contents have to stay under ${formatBytes(UPLOAD_MAX_UNCOMPRESSED_BYTES)}. Drop the raw video or design files a review does not need.`,
  },
  {
    kind: 'too-many-files',
    test: /UPLOAD_MAX_FILES|too many files/i,
    title: 'The archive has too many files',
    remedy: `An archive can hold ${String(UPLOAD_MAX_FILES)} entries. Remove what the prototype does not load — node_modules and source maps are the usual culprits.`,
  },
  {
    kind: 'too-large',
    test: /UPLOAD_MAX_BYTES|Archive is too large|File too large/i,
    title: 'The archive is over the size limit',
    remedy: `The ZIP itself has to stay under ${formatBytes(UPLOAD_MAX_BYTES)}. Compress the images, or split the prototype into two.`,
  },
  {
    kind: 'storage',
    test: /could not be stored/i,
    title: 'The files could not be stored',
    remedy:
      'The upload reached Atlas but writing it failed, so it was rolled back completely. Nothing was saved — try again in a moment.',
    canRetrySameFile: true,
  },
];

/** Rejections decided by status alone, when no message rule matched. */
const STATUS_RULES: ReadonlyMap<number, RejectionRule> = new Map([
  [
    404,
    {
      kind: 'prototype-missing' as const,
      test: /^/,
      title: 'This prototype no longer exists',
      remedy: 'Somebody deleted it while this dialog was open. Nothing was saved.',
    },
  ],
  [
    409,
    {
      kind: 'concurrent-upload' as const,
      test: /^/,
      title: 'Another upload finished first',
      remedy:
        'Two uploads raced for the same version number and this one lost. Nothing was saved — upload again and it will be numbered after theirs.',
      canRetrySameFile: true,
    },
  ],
  [
    413,
    {
      kind: 'too-large' as const,
      test: /^/,
      title: 'The archive is over the size limit',
      remedy: `The ZIP itself has to stay under ${formatBytes(UPLOAD_MAX_BYTES)}. Compress the images, or split the prototype into two.`,
    },
  ],
]);

const build = (
  rule: RejectionRule,
  detail: string | null,
): UploadRejection => ({
  kind: rule.kind,
  title: rule.title,
  remedy: rule.remedy,
  detail,
  files:
    detail === null || rule.files === undefined
      ? []
      : rule.files(detail).slice(0, MAX_LISTED_FILES),
  canRetrySameFile: rule.canRetrySameFile ?? false,
});

/**
 * Turn whatever the upload rejected with into something worth reading.
 *
 * Never throws and always returns a rejection: an unrecognised failure still
 * carries the server's own sentence, under a headline that does not pretend to
 * know more than it does.
 */
export const classifyUploadRejection = (error: unknown): UploadRejection => {
  if (!isApiError(error)) {
    return {
      kind: 'unknown',
      title: 'The upload did not complete',
      remedy: 'Nothing was saved. Try again.',
      detail: error instanceof Error && error.message !== '' ? error.message : null,
      files: [],
      canRetrySameFile: true,
    };
  }

  /*
   * The three failures that never reached the server, or reached it and were
   * abandoned. None of them says anything about the archive, so none of them
   * should be dressed up as an archive problem.
   */
  if (error.kind === 'canceled') {
    return {
      kind: 'cancelled',
      title: 'Upload cancelled',
      remedy: 'Nothing was saved. The file is still selected if you want to retry.',
      detail: null,
      files: [],
      canRetrySameFile: true,
    };
  }

  if (error.kind === 'network') {
    return {
      kind: 'network',
      title: 'The upload never reached the server',
      remedy: 'Check the connection and try again. Nothing was saved.',
      detail: null,
      files: [],
      canRetrySameFile: true,
    };
  }

  if (error.kind === 'timeout') {
    return {
      kind: 'timeout',
      title: 'The upload timed out',
      remedy:
        'The archive was still being sent when the request gave up. On a slow connection a smaller archive is the reliable fix.',
      detail: null,
      files: [],
      canRetrySameFile: true,
    };
  }

  const detail = error.message;
  const matched = RULES.find((rule) => rule.test.test(detail));

  if (matched !== undefined) {
    return build(matched, detail);
  }

  const byStatus = error.status === null ? undefined : STATUS_RULES.get(error.status);

  if (byStatus !== undefined) {
    return build(byStatus, detail);
  }

  return {
    kind: 'unknown',
    title: 'The upload was refused',
    remedy: 'Nothing was saved.',
    detail,
    files: [],
    canRetrySameFile: false,
  };
};
